import { createSlice } from '@reduxjs/toolkit';
import {
  UNASSIGNED_BRANCH_ID,
  fetchClientListV2,
  fetchClientListWithLienHolder,
  fetchTargetRecoveryRatesByUser,
} from '@rapid-recovery-agency-inc/insightt-shared';
import { TargetRecoveryRateSchema } from './validators';
import { client } from '../../shared/apollo';
import { getDateRange, toServiceBranchId, toServiceBranchIdTRR } from './utils';
import {
  CREATE_ONE_TARGET_RECOVERY_RATES,
  FIND_MANY_TARGET_RECOVERY_RATES,
  UPDATE_ONE_TARGET_RECOVERY_RATES,
} from './queries';
import { fetchTargetRecoveryRates } from './actions';

export const reportsSlice = createSlice({
  name: 'reportsSlice',
  initialState: {
    isLoading: false,
    // TargetRecoveryRate
    targetRecoveryRateError: null,
    targetRecoveryRates: [],
    targetRecoveryRate: null,
    targetRecoveryRateLoading: false,
    // ClientStats
    clientStats: null,
    clientStatsError: null,
    clientStatsLoading: false,
    pastClientStats: null,
  },
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setTargetRecoveryRateError: (state, action) => {
      state.targetRecoveryRateError = action.payload;
    },
    setTargetRecoveryRate: (state, action) => {
      state.targetRecoveryRate = action.payload;
    },
    setClientStats: (state, action) => {
      state.clientStats = action.payload;
    },
    setClientStatsError: (state, action) => {
      state.clientStatsError = action.payload;
    },
    setPastClientStats: (state, action) => {
      state.pastClientStats = action.payload;
    },
    setTargetRecoveryRateLoading: (state, action) => {
      state.targetRecoveryRateLoading = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setPastClientStats,
  setClientStatsError,
  setClientStats,
  setIsLoading,
  setTargetRecoveryRateError,
  setTargetRecoveryRate,
  setTargetRecoveryRateLoading,
} = reportsSlice.actions;
export const { reducer: reportsReducer } = reportsSlice;

export const saveTargetRecoveryRateAction = async (data, dispatch, callback) => {
  try {
    TargetRecoveryRateSchema.validateSync(data);
  } catch (e) {
    const allErrors = e.errors.join('\n');
    return dispatch(setTargetRecoveryRateError(allErrors));
  }
  const { userId, branchIds, dateRanges, targetRecoveryRate, clientId } = data;
  const variablesList = [];
  branchIds.forEach((branchId) => {
    dateRanges.forEach((duration) => {
      const branchCreate =
        toServiceBranchId(branchId) === undefined ? undefined : { connect: { id: toServiceBranchId(branchId) } };
      const branchWhere =
        toServiceBranchId(branchId) === undefined ? null : { equals: toServiceBranchId(branchId) };
      variablesList.push({
        where: {
          branchId: branchWhere,
          clientId: { equals: clientId },
          duration: { equals: duration },
          userId: { equals: userId },
        },
        create: {
          branch: branchCreate,
          clientId,
          duration,
          user: { connect: { id: userId } },
          targetRecoveryRate,
        },
        update: { targetRecoveryRate: { set: targetRecoveryRate } },
      });
    });
  });
  dispatch(setTargetRecoveryRateLoading(true));
  try {
    await Promise.all(
      variablesList.map(async (variables) => {
        try {
          await client.mutate({
            mutation: CREATE_ONE_TARGET_RECOVERY_RATES,
            variables: { ...variables },
          });
          return;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          // console.log('saveTargetRecoveryRateAction:createError:', error);
        }
        // We try to update if create fails
        const { data } = await client.query({
          query: FIND_MANY_TARGET_RECOVERY_RATES,
          variables: { ...variables },
        });

        if (data.targetRecoveryRates.length === 0) {
          const error = new Error('No target recovery rate found. This should not happen.');
          dispatch(setTargetRecoveryRateError(String(error)));
          throw error;
        }

        try {
          await client.mutate({
            mutation: UPDATE_ONE_TARGET_RECOVERY_RATES,
            variables: {
              update: { ...variables.update },
              where: { id: data.targetRecoveryRates[0].id },
            },
          });
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          //  throw error;
        }
      }),
    );
  } catch (e) {
    dispatch(setTargetRecoveryRateError(e));
  } finally {
    branchIds.forEach((branchId) => {
      const branchNumber = Number(branchId);

      fetchTargetRecoveryRates(userId, branchNumber, 'network-only');
    });
    dispatch(setTargetRecoveryRateLoading(false));
  }
  callback();
};

export const fetchTargetRecoveryRateAction = async (input, dispatch) => {
  if (Number(input.branchId) === UNASSIGNED_BRANCH_ID) {
    return dispatch(setTargetRecoveryRate(null));
  }
  input.branchId = toServiceBranchIdTRR(input.branchId);
  const data = await fetchTargetRecoveryRatesByUser(client, input.userId, input);

  dispatch(setTargetRecoveryRate(data[0] ?? null));
};

export const fetchClientsStatsAction = async (input, dispatch) => {
  const { datePeriod, timezone, branchId, clientId, lienholderId, isEnabled } = input;
  const { startDate, endDate } = getDateRange(datePeriod, timezone);
  const startDateString = startDate.format('YYYY-MM-DD');
  const endDateString = endDate.format('YYYY-MM-DD');
  const companyStartDate = startDate.toISOString();
  const companyEndDate = endDate.toISOString();

  try {
    dispatch(setIsLoading(true));
    let result;
    if (isEnabled) {
      const input = {
        startDate: startDateString,
        endDate: endDateString,
        startDateTime: companyStartDate,
        endDateTime: companyEndDate,
        rdnStartDateTime: companyStartDate,
        rdnEndDateTime: companyEndDate,
        branchId: toServiceBranchId(branchId),
        lenderClientId: clientId,
        lienholderClientId: lienholderId,
        clientType: lienholderId ? 'lienholder' : 'lender',
      };

      result = await fetchClientListWithLienHolder(client, input);
    } else {
      result = await fetchClientListV2(
        client,
        startDateString,
        endDateString,
        companyStartDate,
        companyEndDate,
        companyStartDate,
        companyEndDate,
        toServiceBranchId(branchId),
        clientId,
      );
    }

    let clientIndex;
    if (lienholderId) {
      clientIndex = lienholderId;
    } else {
      clientIndex = clientId;
    }

    return dispatch(setClientStats(result.get(clientIndex)));
  } catch (e) {
    dispatch(setClientStatsError(e));
  } finally {
    dispatch(setIsLoading(false));
  }
};

export const fetchPastClientsStatsAction = async (input, dispatch) => {
  const { timezone, branchId, clientId, datePeriod, lienholderId, isEnabled } = input;
  const { startDate, endDate } = getDateRange(datePeriod, timezone);
  const dates = [
    {
      year: startDate.year(),
      startDateString: startDate.format('YYYY-MM-DD'),
      endDateString: endDate.format('YYYY-MM-DD'),
      companyStartDate: startDate.toISOString(),
      companyEndDate: endDate.toISOString(),
    },
  ];
  for (let i = 0; i < 3; i++) {
    const pastStartDate = startDate.clone().subtract(i + 1, 'years');
    const pastEndDate = endDate.clone().subtract(i + 1, 'years');
    dates.push({
      year: pastStartDate.year(),
      startDateString: pastStartDate.format('YYYY-MM-DD'),
      endDateString: pastEndDate.format('YYYY-MM-DD'),
      companyStartDate: pastStartDate.toISOString(),
      companyEndDate: pastEndDate.toISOString(),
    });
  }
  try {
    const statsMap = {};
    let result;
    await Promise.all(
      dates.map(async (date) => {
        if (isEnabled) {
          const input = {
            startDate: date.startDateString,
            endDate: date.endDateString,
            startDateTime: date.companyStartDate,
            endDateTime: date.companyEndDate,
            rdnStartDateTime: date.companyStartDate,
            rdnEndDateTime: date.companyEndDate,
            branchId: toServiceBranchId(branchId),
            lenderClientId: clientId,
            lienholderClientId: lienholderId,
            clientType: lienholderId ? 'lienholder' : 'lender',
          };

          result = await fetchClientListWithLienHolder(client, input);
        } else {
          result = await fetchClientListV2(
            client,
            date.startDateString,
            date.endDateString,
            date.companyStartDate,
            date.companyEndDate,
            date.companyStartDate,
            date.companyEndDate,
            toServiceBranchId(branchId),
            clientId,
          );
        }

        let clientIndex;
        if (lienholderId) {
          clientIndex = lienholderId;
        } else {
          clientIndex = clientId;
        }

        statsMap[date.year] = result.get(clientIndex);
      }),
    );

    return dispatch(setPastClientStats(statsMap));
  } catch (e) {
    dispatch(setClientStatsError(e));
  }
};
